import {reaction, observable, computed, action} from 'mobx';
import {autobind, lazyInitialize, nonenumerable} from 'core-decorators';
import objectAssignDeep from 'utils/object-assign-deep';
import {fromCallback} from 'promise-cb';

import APIRequestState from '../_util/api-request';
import BraintreeState from './braintree';
import CheckoutComState from './checkout-com';

import {withI18n} from 'spa/Components/Context';

export const APPLE_PAY_METHOD = {
  id: 'applePay',
  imageUrl: 'https://res.cloudinary.com/huxr6hrje/image/upload/q_80/v1631012123/payment-method-icons/payment_apple.svg',
  paymentInstrumentBrand: 'ApplePay',
  paymentInstrumentType: 'applePay',
};

const SUPPORTED_PAYMENT_NETWORKS = [
  'visa',
  'masterCard',
  'amex',
];

export const PAYMENT_TYPES = {
  APPLE_PAY: 'applePay',
  CARD: 'card',
  PAYPAL: 'paypal',
};

export const PAYMENT_INSTRUMENT_BRANDS = {
  AMEX: 'Amex',
  AMERICAN_EXPRESS: 'American Express',
  MASTERCARD: 'Mastercard',
  VISA: 'Visa',
};

class LoadPaymentMethodsState extends APIRequestState {
  get method() { return 'GET'; }
  get url() { return '/billing/payment-methods'; }
}

class LoadPaymentGatewaysState extends APIRequestState {
  get method() { return 'GET'; }
  get url() { return '/billing/clients'; }
}

class AddPaymentMethodState extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/billing/payment-methods'; }
}

class AddPaymentMethodV2State extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/v2/billing/payment-methods'; }
}

class CompleteAdd3dsChallengeState extends APIRequestState {
  putRequest(...params) {
    return this.ctx.sdk.post('/v2/billing/payment-methods/complete-challenge', ...params);
  }
}

class RemovePaymentMethodState extends APIRequestState {
  get method() { return 'DELETE'; }
  get url() { return '/billing/payment-methods'; }
}

@withI18n
class BillingState {
  @observable _load;
  @observable _loadGateways;
  @observable _add;
  @observable _addv2;
  @observable _delete;
  @observable canUseApplePay = false;
  @observable canUsePaypal = false;
  @observable charge = 0;
  @observable chargeCity;
  @observable chargeCountry;
  @observable paymentInfo = {
    /* card, paypal or applepay - PAYMENT_TYPES */
    paymentOption: null,
    paymentToken: null,
  };
  @observable paymentMethodChanged = false;

  @nonenumerable @lazyInitialize braintree = new BraintreeState(this.ctx);
  @nonenumerable @lazyInitialize checkout = new CheckoutComState(this.ctx);

  @lazyInitialize @nonenumerable addCardComplete3DS = new CompleteAdd3dsChallengeState(this.ctx);

  @computed get isLoading() { return this._load && this._load.isLoading || false; }
  @computed get isLoaded() { return this._load && !!this._load.response || false; }
  @computed get error() { return this._load && this._load.error || null; }

  load(...args) {
    if (!this._load) this._load = new LoadPaymentMethodsState(this.ctx);
    return this._load.load(...args);
  }

  maybeLoad(...args) {
    if (!this._load) this._load = new LoadPaymentMethodsState(this.ctx);
    return this._load.maybeLoad(...args);
  }

  maybeLoadGateways(...args) {
    if (!this._loadGateways) this._loadGateways = new LoadPaymentGatewaysState(this.ctx);
    return this._loadGateways.maybeLoad(...args);
  }

  add(...args) {
    if (!this._add) this._add = new AddPaymentMethodState(this.ctx);
    return this._add.load(...args);
  }

  addv2(...args) {
    if (!this._addv2) this._addv2 = new AddPaymentMethodV2State(this.ctx);
    return this._addv2.load(...args);
  }

  delete(...args) {
    if (!this._delete) this._delete = new RemovePaymentMethodState(this.ctx);
    return this._delete.delete(...args);
  }

  reset() {
    if (this._load && this._load.reset) this._load.reset();

    if (this._loadGateways && this._loadGateways.reset) this._loadGateways.reset();
  }

  @observable paymentMethods = [];
  @observable paymentGateways = [];

  @action getGatewayInfo(gatewayName) {
    return this.paymentGateways.filter(gateway => gateway.gateway === gatewayName);
  }

  @action tokenizeCard(cardData) {
    const {gateway} = this.cardGateway;

    return this[gateway].tokenizeCard(cardData);
  }

  @action async verify3DS(options, gatewayInfo) {
    const {gateway} = this.cardGateway;

    const currentGateway = gatewayInfo || gateway;

    const isGatewayInitialised = this.initialisedGateways[currentGateway];

    // If gateway has already been initialised, we just need to verify3DS
    if (isGatewayInitialised) {
      return this[currentGateway].verify3DS(options);
    }

    // If gateway hasn't been initialised yet, we need to initialise it before we do the 3ds check
    return new Promise(((resolve, reject) => {
      // Get the token of the gateway to initialise it
      const newGatewayInfo = this.getGatewayInfo(gatewayInfo);

      this[currentGateway].initGateway(newGatewayInfo, this.countryId, this.userId)
        .then(() => resolve(this[currentGateway].verify3DS(options)))
        .catch(error => reject(new Error(error)));
    }));
  }

  @action tokenizePaypal(homeCountry_id) {
    const {gateway} = this.paypalGateway;

    return this[gateway].tokenizePaypal(homeCountry_id);
  }

  @autobind initGateway(countryId, userId, paymentType) {
    this.countryId = countryId;
    this.userId = userId;

    switch (paymentType) {
    case 'card':
      return this.startGateway(countryId, userId, paymentType, this.cardGateway);
    case 'applePay':
      return this.startGateway(countryId, userId, paymentType, this.applePayGateway);
    case 'paypal':
      return this.startGateway(countryId, userId, paymentType, this.paypalGateway);
    default:
      return undefined;
    }
  }

  @autobind startGateway(countryId, userId, paymentType, gatewayInfo) {
    const {gateway} = gatewayInfo;

    if (this.initialisedGateways && !this.initialisedGateways[gateway]) {
      return this[gateway].initGateway(gatewayInfo, countryId, userId)
        .then(() => {
          // Store gateway as initialised
          this.initialisedGateways[gateway] = true;
        });
    }
  }

  @action('closePaypal - BillingState')
  closePaypal() {
    if (!this.paypalGateway) return null;

    const {gateway} = this.paypalGateway;
    if (!gateway) return null;

    return this[gateway].closePaypal();
  }

  @action('resetPaypal - BillingState')
  resetPaypal() {
    if (!this.paypalGateway) return null;

    this.closePaypal();
    const {gateway} = this.paypalGateway;
    if (!gateway) return null;

    return this[gateway].resetPaypal();
  }

  /*
   *  createPaymentRequest - create the payment request object to initialise ApplePay session
   */
  @action('createPaymentRequest - BillingState')
  createPaymentRequest(data) {
    if (!this.applePayGateway) return null;

    const {gateway} = this.applePayGateway;
    if (!gateway) return null;

    const [gatewayInfo] = this.getGatewayInfo(gateway);
    if (!gatewayInfo) return null;

    const {currency_id, total} = data;

    const {supportedNetworks} = this;
    const {i18n} = this.ctx;

    const countryId = i18n.iso3166Region(this.countryId);

    return this[gateway].createPaymentRequest({
      supportedNetworks,
      capabilities: ['supports3DS'],
      country_id: countryId,
      currency_id,
      total,
    });
  }

  /*
   *  validateApplePay - validate the Apple Pay merchant website
   */
  @action('validateApplePay - BillingState')
  validateApplePay(data) {
    if (!this.applePayGateway) return null;

    const {gateway} = this.applePayGateway;
    if (!gateway) return null;

    return this[gateway].validateApplePay(data, this.countryId);
  }

  /*
   *  getApplePayPaymentData - get the object with the payment data
   */
  @action('validateApplePay - BillingState')
  getApplePayPaymentData(data) {
    if (!this.applePayGateway) return null;

    const {gateway} = this.applePayGateway;
    if (!gateway) return null;

    return new Promise(((resolve, reject) => {
      this[gateway].getApplePayPaymentData(data)
        .then((response) => {
          resolve(response);
        })
        .catch(error => reject(new Error(error)));
    }));
  }

  /*
   *  updateCharge - update charge value
   */
  @autobind
  @action('updateCharge - BillingState')
  updateCharge(data) {
    this.charge = data;
  }

  /*
   *  resetCharge - update charge value
   */
  @autobind
  @action('resetCharge - BillingState')
  resetCharge(data) {
    this.charge = 0;
  }

  /*
   *  updateChargeCity - update chargeCity value
   */
  @autobind
  @action('updateChargeCity - BillingState')
  updateChargeCity(data) {
    this.chargeCity = data;
  }

  /*
   *  resetChargeCity - reset chargeCity value
   */
  @autobind
  @action('resetChargeCity - BillingState')
  resetChargeCity(data) {
    this.chargeCity = null;
  }

  /*
   *  updateChargeCountry - update chargeCountry value
   */
  @autobind
  @action('updateChargeCountry - BillingState')
  updateChargeCountry(data) {
    this.chargeCountry = data;
  }

  /*
   *  resetChargeCountry - reset chargeCountry value
   */
  @autobind
  @action('resetChargeCountry - BillingState')
  resetChargeCountry(data) {
    this.chargeCountry = null;
  }

  /*
   *  updatePaymentMethodChanged - update charge value
   */
  @autobind
  @action('updatePaymentMethodChanged - BillingState')
  updatePaymentMethodChanged(data) {
    this.paymentMethodChanged = data;
  }

  /*
   *  setPaymentToken - set the chosen payment token
   */
  @autobind
  @action('setPaymentToken - BillingState')
  setPaymentToken(data) {
    this.paymentInfo.paymentToken = data;
  }

  /*
   *  setPaymentOption - set the chosen payment option
   */
  @autobind
  @action('setPaymentOption - BillingState')
  setPaymentOption(data) {
    this.paymentInfo.paymentOption = data;
  }

  /*
   *  resetPaymentInfo - reset payment info
   */
  @autobind
  @action('resetPaymentInfo - BillingState')
  resetPaymentInfo() {
    this.paymentInfo.paymentToken = null;
    this.paymentInfo.paymentOption = null;
  }

  @autobind
  @action('resetChargeData - BillingState')
  resetChargeData() {
    this.charge = 0;
    this.chargeCity;
    this.chargeCountry;
    this.paymentInfo = {
      paymentOption: null,
      paymentToken: null,
    };
    this.paymentMethodChanged = false;
  }

  @autobind
  @action('loadPaymentMethods - BillingState')
  loadPaymentMethods() {
    this.maybeLoad();
  }

  @autobind
  @action('addNewCard - BillingState')
  addNewCardV2(cardData, user, successCallback, errorCallback, addCardReceivedChallengeCallback) {
    const {token} = cardData;

    if (!token) {
      errorCallback({code: 'CLIENT_INVALID_CARD'});

      return;
    }

    const chargeTokenData = {
      gateway: this.cardGateway.gateway,
      gatewayTokenizationType: 'card',
      paymentMethodNonce: token,
    };

    this.addv2({}, {
      chargeTokenData,
      billingPostcode: cardData.billing_address.zip,
    })
      .then((response) => {
        this.loadPaymentMethods();
        successCallback(response);
      }, (error) => {
        if (error.json && error.json.code === '3D_SECURE_REQUIRED_FOR_PAYMENT') {
          // This means the card needs 3DS verification
          this.onAddCardRequest3dsChallenge(
            error,
            successCallback,
            errorCallback,
            addCardReceivedChallengeCallback,
          );

          return;
        }

        errorCallback(error);
      });
  }

  @autobind
  @action('addNewCard - BillingState')
  addNewCard(cardData, user, successCallback, errorCallback, addCardReceivedChallengeCallback) {
    this.tokenizeCard({...cardData, name: user.name})
      .then((card) => {
        const {nonce} = card;

        if (!nonce) {
          errorCallback({code: 'CLIENT_INVALID_CARD'});

          return;
        }

        const chargeTokenData = {
          gateway: this.cardGateway.gateway,
          gatewayTokenizationType: 'card',
          paymentMethodNonce: nonce,
        };

        this.addv2({}, {
          chargeTokenData,
          billingPostcode: cardData.billingAddress.postalCode,
        })
          .then((response) => {
            this.loadPaymentMethods();
            successCallback(response);
          }, (error) => {
            if (error.json && error.json.code === '3D_SECURE_REQUIRED_FOR_PAYMENT') {
              // This means the card needs 3DS verification
              this.onAddCardRequest3dsChallenge(
                error,
                successCallback,
                errorCallback,
                addCardReceivedChallengeCallback,
              );

              return;
            }

            errorCallback(error);
          });
      }, (error) => {
        if (error.code === 'CLIENT_REQUEST_ERROR') {
          error.code = 'CLIENT_INVALID_CARD';
        }

        errorCallback(error);
      });
  }

  @autobind
  @action('onCompleteAdd3dsCheck - BillingState')
  onCompleteAdd3dsCheck(charge_id, successCallback, errorCallback) {
    this.addCardComplete3DS.put({
      charge_id,
    })
      .then((response) => {
        this.loadPaymentMethods();
        successCallback(response);
      }, (error) => {
        errorCallback(error);
      });
  }

  /** @private internal method to initiate the 3d secure challenge from a challenge response */
  @autobind
  @action('onAddCardRequest3dsChallenge - BillingState')
  onAddCardRequest3dsChallenge(error, successCallback, errorCallback, addCardReceivedChallengeCallback) {
    addCardReceivedChallengeCallback();

    this.verify3DS({
      amount: 0,
      nonce: error.json.error && error.json.error.nonce,
    }, error.json.gatewayName)
      .then((response) => {
        const {query: {charge_id} = {}} = response;

        this.onCompleteAdd3dsCheck(charge_id, successCallback, errorCallback);
      }, (error3DS) => {
        errorCallback(error3DS);
      })
      .catch((err) => {
        errorCallback(err);
      });
  }

  @computed get isValidatingApplePay() {
    if (!this.applePayGateway) return false;

    const {gateway} = this.applePayGateway;
    if (!gateway) return false;

    return this[gateway].isValidatingApplePay;
  }

  /*
   *  canMakeApplePayments - check if browser can use ApplePay
   */
  @computed get canMakeApplePayments() {
    if (!this.applePayGateway) return null;

    const {gateway} = this.applePayGateway;
    if (!gateway) return null;

    return this[gateway].canMakeApplePayments;
  }

  /*
   *  cards - returns the cards saved in the user's account
   */
  @computed get cards() {
    if (!this.paymentMethods) return null;

    return this.paymentMethods
      .filter(card => card.paymentInstrumentType !== 'paypal');
  }

  /*
   *  paypalAccounts - returns the paypal accounts saved in the user's account
   */
  @computed get paypalAccounts() {
    if (!this.paymentGateways) return null;

    return this.paymentMethods
      .filter(card => card.paymentInstrumentType === 'paypal');
  }

  /*
   *  cardGateway - get payment gateway for cards
   */
  @computed get cardGateway() {
    if (!this.paymentGateways) return null;

    const cardGateways = this.paymentGateways
      .filter(gateway => gateway.availablePaymentMethodTokenizationTypes.indexOf('card') >= 0);

    return cardGateways[0];
  }

  /*
   *  paypalGateway - get payment gateway for paypal
   */
  @computed get paypalGateway() {
    if (!this.paymentGateways) return null;

    const paypalGateWays = this.paymentGateways
      .filter(gateway => gateway.availablePaymentMethodTokenizationTypes.indexOf('paypal') >= 0);

    return paypalGateWays[0];
  }

  /*
   *  applePayGateway - get payment gateway for ApplePay
   */
  @computed get applePayGateway() {
    if (!this.paymentGateways) return null;

    const applePayGateWays = this.paymentGateways
      .filter(gateway => gateway.availablePaymentMethodTokenizationTypes.indexOf('apple-pay') >= 0);

    return applePayGateWays[0];
  }

  @computed get paypalGatewayReady() {
    if (!this.paypalGateway) return null;

    const {gateway} = this.paypalGateway;

    return this[gateway].clientToken;
  }

  abort() {
    if (this._add) {
      this._add.abort();
    }

    if (this._addv2) {
      this._addv2.abort();
    }

    if (this._delete) {
      this._delete.abort();
    }

    if (this._load) {
      this._load.abort();
    }
  }

  @nonenumerable
  listeners = [];

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    const {config, i18n, session} = ctx;

    this.initialisedGateways = {};

    this.supportedNetworks = SUPPORTED_PAYMENT_NETWORKS;

    this.listeners.push(
      reaction(
        () => this._load && this._load.response && this._load.response.paymentMethods,
        (paymentMethods) => {
          this.paymentMethods = Array.from(paymentMethods || []);
        },
        {
          name: 'Add cards to list when they\'re loaded',
          compareStructural: true,
        },
      ),
      reaction(
        () => this._loadGateways && this._loadGateways.response && this._loadGateways.response.gateways,
        (gateways) => {
          this.paymentGateways = Array.from(gateways || []);
        },
        {
          name: 'Add payment gateways to list when they\'re loaded',
          compareStructural: true,
        },
      ),
      reaction(
        () => this._add && this._add.response && this._add.response.paymentMethod,
        (paymentMethod) => {
          if (paymentMethod) {
            this.paymentMethods.unshift(paymentMethod);
            this._add.reset();
          }
        },
        {
          name: 'Add new card to list of cards when it\'s added to the user\'s vault',
        },
      ),
      reaction(
        () => this._addv2 && this._addv2.response && this._addv2.response.paymentMethod,
        (paymentMethod) => {
          if (paymentMethod) {
            this.paymentMethods.unshift(paymentMethod);
            this._addv2.reset();
          }
        },
        {
          name: 'Add new card to list of cards when it\'s added to the user\'s vault',
        },
      ),
      reaction(
        () => session && session.user,
        (user) => {
          if (user) {
            // TODO this can all be removed when the booking-next flow goes live - you'll only want to initialise the gateways.
            // anything that loads payment methods in this state here should be removed
            this.maybeLoad();

            this.maybeLoadGateways({
              country_id: i18n.umRegion,
            });
          }
        },
        {
          fireImmediately: true,
          name: 'Load gateways and update payment methods when user signs in',
        },
      ),
      reaction(
        () => [this.paymentGateways, this.paymentInfo.paymentOption],
        ([gateways, paymentOption]) => {
          if (gateways.length && session.user) {
            const {id} = session.user;
            const countryId = i18n.umRegion;

            if (id && countryId && paymentOption) {
              this.initGateway(countryId, id, paymentOption);
            }
          }
        },
        {
          name: 'Initialize a gateway',
          fireImmediately: true,
        },
      ),
      reaction(
        () => (this.paymentGateways && this.paymentGateways.length > 0) && this.charge && session && session.user,
        () => {
          if (this.canUseApplePay && session.user && this.paymentGateways.length) {
            this.initGateway(i18n.umRegion, session.user.id, PAYMENT_TYPES.APPLE_PAY);
          }
        },
        {
          fireImmediately: true,
          name: 'Initialise ApplePay payment gateway',
        },
      ),
      reaction(
        () => this.canMakeApplePayments,
        () => {
          if (this.canMakeApplePayments && this.charge > 0 && !this.paymentMethodChanged) {
            this.setPaymentOption(PAYMENT_TYPES.APPLE_PAY);
          }
        },
        {
          fireImmediately: true,
          name: 'Set ApplePay as payment option',
        },
      ),
      reaction(
        () => [this.paymentMethods && this.paymentMethods.length, this.charge, this.chargeCity, this.canUsePaypal, this.canUseApplePay],
        () => {
          if (!this.paymentMethodChanged) {
            const {paymentMethods} = this;

            // If Apple pay is available we need to use it by default
            if (this.canMakeApplePayments && this.charge > 0 && this.canUseApplePay) {
              this.setPaymentOption(PAYMENT_TYPES.APPLE_PAY);
              this.newCardForm = false;
            } else if (paymentMethods && paymentMethods.length) {
              const [firstPaymentMethod] = paymentMethods;
              // select the most recent card, if paypal is disabled find next not paypal payment method
              const notPaypalPaymentMethod = paymentMethods.find(method => method.paymentInstrumentType !== PAYMENT_TYPES.PAYPAL) || {};

              this.setPaymentOption(this.canUsePaypal ? firstPaymentMethod.paymentInstrumentType : notPaypalPaymentMethod.paymentInstrumentType);
              this.setPaymentToken(this.canUsePaypal ? firstPaymentMethod.id : notPaypalPaymentMethod.id);

              this.newCardForm = false;
            } else {
              this.newCardForm = true;

              this.setPaymentOption(PAYMENT_TYPES.CARD);
              this.setPaymentToken('');
            }
          }
        },
        {
          fireImmediately: true,
          name: 'Select applicable payment method',
        },
      ),
      reaction(
        () => this.charge,
        (amount) => {
          if (amount === 0) {
            const {paymentInfo, paymentMethods} = this;

            if (paymentMethods && paymentMethods.length && paymentInfo.paymentOption === PAYMENT_TYPES.APPLE_PAY) {
              const id = paymentMethods[0].id; //eslint-disable-line prefer-destructuring
              // select the most recent card
              this.setPaymentOption(paymentMethods[0].paymentInstrumentType === PAYMENT_TYPES.PAYPAL && this.canUsePaypal ? PAYMENT_TYPES.PAYPAL : PAYMENT_TYPES.CARD);
              this.setPaymentToken(id);
            } else if (!paymentMethods || !paymentMethods.length) {
              this.newCardForm = true;

              this.setPaymentOption(PAYMENT_TYPES.CARD);
              this.setPaymentToken('');
            }
          }
        },
        {
          fireImmediately: true,
          name: 'Deselect Apple Pay if charge value reaches 0',
        },
      ),
      reaction(
        () => [this.chargeCity, this.chargeCountry],
        ([chargeCity, chargeCountry]) => {
          if (chargeCity && chargeCountry) {
            this.canUseApplePay = config.getValue('billing.apple-pay-enabled', chargeCountry, chargeCity);
            this.canUsePaypal = config.getValue('billing.paypal-enabled', chargeCountry, chargeCity);
          }
        },
        {
          fireImmediately: true,
          name: 'Get config values for Paypal and ApplePay',
        },
      ),
    );
  }

  @action deserialize(value) {
    const {
      _load, _add, _addv2, _delete, ...rest
    } = value;
    objectAssignDeep(this, rest);

    if (_load) {
      this._load = new LoadPaymentMethodsState(this.ctx);
      objectAssignDeep(this._load, _load);
    }

    if (_add) {
      this._add = new AddPaymentMethodState(this.ctx);
      objectAssignDeep(this._add, _add);
    }

    if (_addv2) {
      this._addv2 = new AddPaymentMethodState(this.ctx);
      objectAssignDeep(this._addv2, _addv2);
    }

    if (_delete) {
      this._delete = new RemovePaymentMethodState(this.ctx);
      objectAssignDeep(this._delete, _delete);
    }
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default BillingState;



// WEBPACK FOOTER //
// ./spa/state/payments/billing.jsx